/**
 * useSharedTaskDateSync — Layer 1: Client-side reactive sync for shared realm tasks.
 *
 * Maintains `startAtIndex` entries for tasks in followed shared containers.
 * Entries are CREATED by:
 * - Layer 2 (DO-to-DO fan-out via Dexie Cloud sync) for other members' changes
 * - Normal scheduleTask flow when the user schedules a shared task themselves
 *
 * This hook handles CLEANUP:
 * - Task completed/deleted → delete corresponding startAtIndex entry
 * - User opts out (showInDateViews → 0) → delete all entries for that container
 *
 * CRUD lifecycle:
 * - Task completed/deleted → Delete startAtIndex entry
 * - User opts out → Delete all entries for that container's tasks
 * - User opts in → entries created by Layer 2 (DO pipeline) on next change
 *
 * Must be mounted in AppLayout (Tier 2) — runs only when app is in foreground.
 */

import { useEffect, useRef } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { ObjectStatus } from "@totodo/operations"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Debounce to coalesce rapid changes (e.g. bulk complete). */
const DEBOUNCE_MS = 300

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSharedTaskDateSync(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const runningRef = useRef(false)

  // 1. Get all container IDs with showInDateViews=1
  const [followedContainerIds] = useLiveQueryImmediate(async () => {
    const prefs = await db.sharedViewPref
      .filter((p) => p.showInDateViews === 1 && p.scope === "container")
      .toArray()
    return prefs.map((p) => p.id.slice(1)) // Remove "#" prefix
  }, ["dateSync-containers"], [])

  // 2. Get per-task IDs with showInDateViews=1
  const [followedTaskIds] = useLiveQueryImmediate(async () => {
    const prefs = await db.sharedViewPref
      .filter((p) => p.showInDateViews === 1 && p.scope === "task")
      .toArray()
    return prefs.map((p) => p.id.slice(1))
  }, ["dateSync-tasks"], [])

  // 3. Watch ONLY startAtIndex entries for tasks in followed shared containers/tasks.
  //    Personal tasks (own realm / root/#* pseudo-containers) are NEVER managed here.
  const [sharedEntryIds] = useLiveQueryImmediate(async () => {
    const allFollowed = [
      ...(followedContainerIds ?? []),
      ...(followedTaskIds ?? []),
    ]
    if (allFollowed.length === 0) return []

    // Primary-key prefix range: one getAll(). A Dexie .filter() here walked a
    // cursor with a main-thread round trip per entry.
    const entries = await db.startAtIndex.where(":id").startsWith("#").toArray()

    // Only include entries whose task actually lives in a followed container or is explicitly followed.
    // One bulkGet for every task instead of one awaited get() per entry: that
    // was ~700 sequential transactions per run, and this query re-runs on any
    // write to any of those tasks — enough to keep the main thread busy while
    // the app sat idle.
    const followedTasks = new Set(followedTaskIds ?? [])
    const followedContainers = new Set(followedContainerIds ?? [])
    const lookupEntries = entries.filter((e) => !followedTasks.has(e.id!.slice(1)))
    const lookedUp = await db.totodo.bulkGet(lookupEntries.map((e) => e.id!.slice(1)))
    const taskByEntryId = new Map(lookupEntries.map((e, i) => [e.id!, lookedUp[i]]))
    const ids: string[] = []
    for (const entry of entries) {
      const taskId = entry.id!.slice(1)
      // Quick check: is this task explicitly followed?
      if (followedTasks.has(taskId)) {
        ids.push(entry.id!)
        continue
      }
      // Otherwise check if its container is followed (requires a task lookup)
      const task = taskByEntryId.get(entry.id!)
      if (!task) {
        // Task not found — do NOT treat as orphan here. The orphanedIndexCleanup
        // background task handles this safely (with sync gate). Deleting here
        // risks data loss during partial sync.
        continue
      }
      // Skip personal tasks entirely
      if (task.realmId === db.cloud.currentUserId || task.parentPath.startsWith("root/#")) {
        continue
      }
      // Use realmId (not parentPath) — sections have parentPath="root/sect-xxx"
      // but the pref is keyed by container (project) ID from realmId.
      const containerId = task.realmId?.replace("rlm~", "") ?? ""
      if (followedContainers.has(containerId)) {
        ids.push(entry.id!)
      }
    }
    return ids
  }, ["dateSync-entries", followedContainerIds, followedTaskIds], [])

  // 4. Reconcile: delete entries for tasks that are no longer qualifying
  useEffect(() => {
    if (!sharedEntryIds?.length) return

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (runningRef.current) return
      runningRef.current = true
      cleanupStaleEntries(
        sharedEntryIds,
        followedContainerIds ?? [],
        followedTaskIds ?? [],
      )
        .catch((err) =>
          console.warn("[shared-task-date-sync] cleanup error:", err),
        )
        .finally(() => {
          runningRef.current = false
        })
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [sharedEntryIds, followedContainerIds, followedTaskIds])
}

// ---------------------------------------------------------------------------
// Cleanup logic
// ---------------------------------------------------------------------------

async function cleanupStaleEntries(
  entryIds: string[],
  containerIds: string[],
  taskIds: string[],
): Promise<void> {
  const toDelete: string[] = []
  const currentUserId = db.cloud.currentUserId
  // One bulkGet instead of an awaited get() per entry (see the entries query).
  const tasks = await db.totodo.bulkGet(entryIds.map((id) => id.slice(1)))

  for (let i = 0; i < entryIds.length; i++) {
    const entryId = entryIds[i]!
    const taskId = entryId.slice(1) // Remove "#" prefix
    const task = tasks[i]

    if (!task) {
      // Task not found — do NOT delete. Could be a sync lag or partial pull.
      // The orphanedIndexCleanup background task handles true orphans safely.
      continue
    }

    // Skip personal tasks — this hook only manages entries for shared realm tasks.
    // Personal tasks have realmId = currentUser or live in personal pseudo-containers.
    if (
      task.realmId === currentUserId ||
      task.parentPath.startsWith("root/#")
    ) {
      continue
    }

    // Task completed or deleted → delete entry
    if (task.isDeleted || task.status !== ObjectStatus.Unfinished) {
      toDelete.push(entryId)
      continue
    }

    // Check if this task is still covered by an active pref
    // Use realmId (not parentPath) — sections have parentPath="root/sect-xxx"
    // but the pref is keyed by container (project) ID from realmId.
    const containerId = task.realmId?.replace("rlm~", "") ?? ""
    const isFollowedViaContainer = containerIds.includes(containerId)
    const isFollowedViaTask = taskIds.includes(taskId)

    if (!isFollowedViaContainer && !isFollowedViaTask) {
      // User opted out of this container/task → delete entry
      toDelete.push(entryId)
    }
  }

  if (toDelete.length) {
    await db.startAtIndex.bulkDelete(toDelete)
  }
}
